import MarkdownText from './MarkdownText'
import QuestSteps, { parseSteps } from './QuestSteps'
import DescriptionSections from './DescriptionSections'

// Read-only "peek" card shown when a [[wiki-link]] is clicked. Renders on top of
// whatever the user was viewing (including an open detail modal) so they can
// check a related entity without losing their place. Links inside the body push
// another preview on top of this one (handled by LinkContext's stack).

const TYPE_META = {
  npc: { icon: '👥', label: 'NPC' },
  location: { icon: '🗺️', label: 'Location' },
  item: { icon: '⚗️', label: 'Item' },
  quest: { icon: '⚔️', label: 'Quest' },
  session: { icon: '📜', label: 'Session' },
  character: { icon: '🎭', label: 'Player Character' },
}

function getImage(e) {
  // Only an uploaded image earns a box; there is no emoji/initials fallback.
  if (e.type === 'npc') return { url: e.avatar_url, round: true }
  if (e.type === 'location') return { url: e.icon_url, round: false }
  if (e.type === 'item') return { url: e.icon_url, round: false }
  if (e.type === 'character') return { url: e.appearance_image_url, round: true }
  return null
}

function getTitle(e) {
  return e.name || e.title || e.character_name || 'Unknown'
}

function getSubtitle(e) {
  switch (e.type) {
    case 'npc':
      return e.title || ''
    case 'location':
      return e.location_type || 'Location'
    case 'item':
      return e.item_type || 'Item'
    case 'quest':
      return [e.quest_type, e.priority].filter(Boolean).join(' · ') || 'Quest'
    case 'session':
      return e.session_date || 'Session'
    case 'character':
      return 'Player Character'
    default:
      return ''
  }
}

function getBody(e) {
  if (e.type === 'session') return e.summary || ''
  if (e.type === 'character') return e.appearance || ''
  return e.full_description || e.description || ''
}

function parseJsonArray(value) {
  if (!value) return []
  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

export default function EntityPreviewModal({ entity, depth = 0, onClose, onOpenFull }) {
  // NPCs and Factions share the 'npc' type but are distinguished by entity_type.
  const meta = entity.type === 'npc' && entity.entity_type === 'faction'
    ? { icon: '🏛️', label: 'Faction' }
    : (TYPE_META[entity.type] || { icon: '🔗', label: entity.type })
  const image = getImage(entity)
  const body = getBody(entity)
  const tags = entity.type === 'npc' ? parseJsonArray(entity.tags) : []
  const keyEvents = entity.type === 'session' ? parseJsonArray(entity.key_events) : []

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(5, 10, 18, 0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10001 + depth * 2,
        padding: '1rem',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          // Explicit opaque colors rather than var(--primary-blue)/var(--secondary-dark) —
          // both are now defined (#0d1520 and #0a0f18, matching these hardcoded stops
          // exactly), so this could switch to the vars with no visual change; left
          // hardcoded so this panel doesn't drift if either token's value changes later.
          // Both stops are fully opaque on purpose: a stacked preview sits on top of a
          // detail modal, and any alpha here bleeds that modal's content through it.
          background: 'linear-gradient(135deg, #0d1520 0%, #0a0f18 100%)',
          border: '2px solid var(--accent-cyan)',
          borderRadius: '12px',
          maxWidth: '680px',
          width: '100%',
          boxShadow: '0 12px 48px rgba(0, 0, 0, 0.7)',
          position: 'relative',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          // Padding lives on the scroll wrapper and footer rather than here, so
          // the scrollbar sits flush against the panel edge instead of 1.75rem
          // inside it; overflow:hidden lets the radius clip its corners. The
          // Close/"Go to card" footer is already out of the scroll area, so
          // unlike the tab modals this one needs no header row.
          overflow: 'hidden',
        }}
      >
        {/* scrollbarGutter keeps the right gutter reserved whether or not the
            content overflows, so short and long previews have equal margins. */}
        <div style={{ overflowY: 'auto', minHeight: 0, padding: '1.75rem 0.75rem 0 1.75rem', scrollbarGutter: 'stable' }}>
        <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'flex-start', marginBottom: '1rem' }}>
          {image?.url && (
            <div
              style={{
                width: '88px',
                height: '88px',
                borderRadius: image.round ? '50%' : '12px',
                background: 'transparent',
                border: '2px solid var(--accent-teal)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '2.25rem',
                color: 'var(--text-primary)',
                overflow: 'hidden',
                flexShrink: 0,
              }}
            >
              <img src={image.url} alt={getTitle(entity)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
          )}

          <div style={{ flex: 1, minWidth: 0 }}>
            <span
              style={{
                display: 'inline-block',
                background: 'rgba(42, 85, 102, 0.4)',
                color: 'var(--accent-cyan)',
                padding: '0.15rem 0.55rem',
                borderRadius: '4px',
                fontSize: '0.7rem',
                fontWeight: 600,
                letterSpacing: '0.03em',
                marginBottom: '0.4rem',
              }}
            >
              {meta.icon} {meta.label}
            </span>
            <h2 style={{ color: 'var(--text-primary)', margin: '0 0 0.25rem', fontSize: '1.4rem' }}>
              {getTitle(entity)}
            </h2>
            {getSubtitle(entity) && (
              <p style={{ color: 'var(--accent-teal)', fontSize: '0.9rem', margin: 0 }}>{getSubtitle(entity)}</p>
            )}
          </div>
        </div>

        {body && (
          <div style={{ color: 'var(--text-secondary)', lineHeight: 1.7, fontSize: '0.95rem' }}>
            <MarkdownText style={{ whiteSpace: 'pre-line' }}>{body}</MarkdownText>
          </div>
        )}

        <DescriptionSections sections={entity.description_sections} />

        {entity.type === 'quest' && (
          <div style={{ marginTop: '1rem' }}>
            <QuestSteps steps={parseSteps(entity.steps)} />
          </div>
        )}

        {keyEvents.length > 0 && (
          <div style={{ marginTop: '1rem' }}>
            <h4 style={{ color: 'var(--accent-teal)', marginBottom: '0.8rem', fontSize: '1rem' }}>Key Events:</h4>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {keyEvents.map((ev, i) => (
                <span key={i} style={{ background: 'rgba(42, 85, 102, 0.3)', color: 'var(--text-primary)', padding: '0.25rem 0.6rem', borderRadius: '4px', fontSize: '0.75rem' }}>
                  {ev}
                </span>
              ))}
            </div>
          </div>
        )}

        {tags.length > 0 && (
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '1rem' }}>
            {tags.map((tag, i) => (
              <span key={i} style={{ background: 'rgba(42, 85, 102, 0.3)', color: 'var(--text-primary)', padding: '0.25rem 0.6rem', borderRadius: '4px', fontSize: '0.75rem' }}>
                {tag}
              </span>
            ))}
          </div>
        )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', padding: '1.5rem 1.75rem 1.75rem', flexShrink: 0 }}>
          {onOpenFull && (
            <button
              onClick={onOpenFull}
              style={{
                background: 'linear-gradient(135deg, #1a5a6e 0%, #2a7a8e 100%)',
                color: '#ffffff',
                border: '1px solid var(--accent-teal)',
                borderRadius: '8px',
                padding: '0.6rem 1.4rem',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 600,
              }}
            >
              Go to card →
            </button>
          )}
          <button
            onClick={onClose}
            style={{
              background: 'linear-gradient(135deg, #3a4550 0%, #2d363f 100%)',
              color: '#ffffff',
              border: '1px solid #5a6570',
              borderRadius: '8px',
              padding: '0.6rem 1.4rem',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: 600,
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
